import { useState, useEffect, useCallback, useRef } from "react";
import VideoPlayer, { type VideoPlayerHandle } from "../../components/VideoPlayer";

type ActivePlayer = "both" | "video1" | "video2";

const SPEEDS = [0.25, 0.5, 1, 2, 4];

const CONTROL_LABEL: Record<ActivePlayer, string> = {
  both: "Both",
  video1: "Video 1",
  video2: "Video 2",
};

function HelpPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end p-2 sm:p-4" onClick={onClose}>
      <div
        className="bg-[#1a1a1a] border border-neutral-700 rounded-lg shadow-2xl p-4 sm:p-5 w-full max-w-sm text-sm text-gray-300 mt-8 sm:mt-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <h2 className="text-base font-semibold text-white">Pose Comparison</h2>
            <p className="text-xs text-neutral-400 mt-0.5">
              Review a movement or compare two side by side to sharpen your form.
            </p>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white text-lg leading-none ml-3">&times;</button>
        </div>
        <div className="space-y-3 text-[13px]">
          <div>
            <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Selected</h3>
            <p className="text-xs text-neutral-400 mb-1">Controls apply to whichever video(s) are selected.</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              <Shortcut keys="Tab">Cycle: Both &rarr; Video 1 &rarr; Video 2</Shortcut>
            </dl>
          </div>
          <div>
            <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Playback</h3>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              <Shortcut keys="Space">Play / Pause selected video(s)</Shortcut>
              <Shortcut keys="- / +">Slow down / Speed up</Shortcut>
            </dl>
          </div>
          <div>
            <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Overlays</h3>
            <p className="text-xs text-neutral-400 mb-1">
              <span className="text-yellow-300">Yellow</span> marks the left side of the body, <span className="text-fuchsia-400">pink</span> marks the right.
            </p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              <Shortcut keys="S">Toggle skeleton</Shortcut>
              <Shortcut keys="A">Toggle angles</Shortcut>
              <Shortcut keys="C">Toggle foot contact indicators</Shortcut>
            </dl>
          </div>
          <div>
            <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Loop</h3>
            <p className="text-xs text-neutral-400 mb-1">Repeat a section of the video.</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              <Shortcut keys="I">Set or clear loop start</Shortcut>
              <Shortcut keys="O">Set or clear loop end</Shortcut>
              <Shortcut keys="X">Clear entire loop</Shortcut>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

function Shortcut({ keys, children }: { keys: string; children: React.ReactNode }) {
  return (
    <>
      <dt><kbd className="inline-block bg-neutral-800 border border-neutral-600 rounded px-1.5 py-0.5 text-[11px] font-mono text-neutral-300 min-w-[24px] text-center">{keys}</kbd></dt>
      <dd className="text-neutral-400">{children}</dd>
    </>
  );
}

function UploadPlaceholder({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="flex-1 min-h-0 flex items-center justify-center bg-[#111] rounded-lg border-2 border-dashed border-neutral-700 hover:border-neutral-500 cursor-pointer transition-colors"
    >
      <div className="flex flex-col items-center gap-3 text-center px-4">
        <svg
          className="w-12 h-12 text-neutral-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
          />
        </svg>
        <p className="text-neutral-500 text-sm">{label}</p>
        <p className="text-neutral-600 text-xs">Supports MP4, MOV, WebM</p>
      </div>
    </div>
  );
}

export default function ComparisonsShow() {
  const [video1, setVideo1] = useState<File | null>(null);
  const [video2, setVideo2] = useState<File | null>(null);
  const [activePlayer, setActivePlayer] = useState<ActivePlayer>("both");
  const [playing1, setPlaying1] = useState(false);
  const [playing2, setPlaying2] = useState(false);
  const [speedIndex, setSpeedIndex] = useState(2); // 1x
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [showAngles, setShowAngles] = useState(true);
  const [showContacts, setShowContacts] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const fileInputBothRef = useRef<HTMLInputElement>(null);
  const fileInput1Ref = useRef<HTMLInputElement>(null);
  const fileInput2Ref = useRef<HTMLInputElement>(null);
  const player1Ref = useRef<VideoPlayerHandle>(null);
  const player2Ref = useRef<VideoPlayerHandle>(null);

  const handleMultiUpload = useCallback(
    (files: FileList, targetSlot: "video1" | "video2" | "both") => {
      if (files.length >= 2) {
        setVideo1(files[0]);
        setVideo2(files[1]);
      } else if (files.length === 1) {
        if (targetSlot === "video1" || targetSlot === "both") {
          setVideo1(files[0]);
        } else {
          setVideo2(files[0]);
        }
      }
    },
    [],
  );

  const togglePlay = useCallback(() => {
    if (activePlayer === "both" || activePlayer === "video1") {
      setPlaying1((p) => !p);
    }
    if (activePlayer === "both" || activePlayer === "video2") {
      setPlaying2((p) => !p);
    }
  }, [activePlayer]);

  const cycleActivePlayer = useCallback(() => {
    setActivePlayer((prev) => {
      if (prev === "both") return "video1";
      if (prev === "video1") return "video2";
      return "both";
    });
  }, []);

  const changeSpeed = useCallback((delta: number) => {
    setSpeedIndex((i) => Math.max(0, Math.min(SPEEDS.length - 1, i + delta)));
  }, []);

  const forActivePlayers = useCallback(
    (fn: (handle: VideoPlayerHandle) => void) => {
      if (activePlayer === "both" || activePlayer === "video1") {
        if (player1Ref.current) fn(player1Ref.current);
      }
      if (activePlayer === "both" || activePlayer === "video2") {
        if (player2Ref.current) fn(player2Ref.current);
      }
    },
    [activePlayer],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === " ") {
        e.preventDefault();
        togglePlay();
      } else if (e.key === "Tab") {
        e.preventDefault();
        cycleActivePlayer();
      } else if (e.key === "=" || e.key === "+") {
        changeSpeed(1);
      } else if (e.key === "-") {
        changeSpeed(-1);
      } else if (e.key === "i" || e.key === "I") {
        forActivePlayers((p) => p.setMarkIn());
      } else if (e.key === "o" || e.key === "O") {
        forActivePlayers((p) => p.setMarkOut());
      } else if (e.key === "x" || e.key === "X") {
        forActivePlayers((p) => p.clearMarks());
      } else if (e.key === "s" || e.key === "S") {
        setShowSkeleton((v) => !v);
      } else if (e.key === "a" || e.key === "A") {
        setShowAngles((v) => !v);
      } else if (e.key === "c" || e.key === "C") {
        setShowContacts((v) => !v);
      } else if (e.key === "?") {
        setHelpOpen((v) => !v);
      } else if (e.key === "Escape") {
        setHelpOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [togglePlay, cycleActivePlayer, changeSpeed, forActivePlayers]);

  const bothEmpty = !video1 && !video2;

  return (
    <div className="flex flex-col h-dvh bg-[#0a0a0a] text-gray-200 font-sans">
      <input
        ref={fileInputBothRef}
        type="file"
        accept="video/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) handleMultiUpload(e.target.files, "both");
          e.target.value = "";
        }}
      />
      <input
        ref={fileInput1Ref}
        type="file"
        accept="video/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) handleMultiUpload(e.target.files, "video1");
          e.target.value = "";
        }}
      />
      <input
        ref={fileInput2Ref}
        type="file"
        accept="video/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) handleMultiUpload(e.target.files, "video2");
          e.target.value = "";
        }}
      />

      {/* Top bar */}
      <div className="bg-[#111] border-b border-neutral-800 text-[12px] sm:text-[13px]">
        {/* Row 1: Selected, Speed, Help */}
        <div className="flex items-center px-2 sm:px-3 py-1.5 gap-1.5 sm:gap-2">
          <button
            onClick={cycleActivePlayer}
            className="bg-transparent border border-neutral-600 rounded px-2 py-0.5 touch-manipulation shrink-0"
            title="Controls apply to whichever video is selected"
          >
            <span className="text-neutral-500">Selected:</span> <strong className="text-yellow-400">{CONTROL_LABEL[activePlayer]}</strong>
          </button>
          <div className="w-px h-4 bg-neutral-700 shrink-0" />
          <span className="text-[10px] text-neutral-500 shrink-0">Speed</span>
          <div className="flex items-center shrink-0">
            <button
              onClick={() => changeSpeed(-1)}
              className="bg-transparent border border-neutral-600 rounded-l w-5 h-5 text-gray-400 touch-manipulation text-[12px]"
            >
              -
            </button>
            <span className="text-neutral-400 text-[12px] min-w-[28px] text-center border-y border-neutral-600 h-5 leading-5 px-1">{SPEEDS[speedIndex]}x</span>
            <button
              onClick={() => changeSpeed(1)}
              className="bg-transparent border border-neutral-600 rounded-r w-5 h-5 text-gray-400 touch-manipulation text-[12px]"
            >
              +
            </button>
          </div>
          {/* Checkboxes inline on sm+ */}
          <div className="hidden sm:contents">
            <div className="w-px h-4 bg-neutral-700 shrink-0" />
            <label
              className="flex items-center gap-1.5 cursor-pointer touch-manipulation shrink-0 select-none text-gray-400"
              onClick={() => setShowSkeleton((v) => !v)}
            >
              <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${showSkeleton ? "bg-green-600 border-green-600 text-white" : "border-neutral-500 bg-transparent"}`}>
                {showSkeleton && <span className="text-[9px] leading-none font-bold">{"\u2713"}</span>}
              </span>
              Skeleton
            </label>
            <label
              className="flex items-center gap-1.5 cursor-pointer touch-manipulation shrink-0 select-none text-gray-400"
              onClick={() => setShowAngles((v) => !v)}
            >
              <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${showAngles ? "bg-green-600 border-green-600 text-white" : "border-neutral-500 bg-transparent"}`}>
                {showAngles && <span className="text-[9px] leading-none font-bold">{"\u2713"}</span>}
              </span>
              Angles
            </label>
            <label
              className="flex items-center gap-1.5 cursor-pointer touch-manipulation shrink-0 select-none text-gray-400"
              onClick={() => setShowContacts((v) => !v)}
            >
              <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${showContacts ? "bg-green-600 border-green-600 text-white" : "border-neutral-500 bg-transparent"}`}>
                {showContacts && <span className="text-[9px] leading-none font-bold">{"\u2713"}</span>}
              </span>
              Contact
            </label>
          </div>
          <div className="flex-1" />
          <button
            onClick={() => setHelpOpen((v) => !v)}
            className="bg-transparent border border-neutral-600 rounded w-6 h-6 text-gray-400 hover:text-white touch-manipulation shrink-0"
            aria-label="Help"
            title="Keyboard shortcuts & help"
          >
            ?
          </button>
        </div>
        {/* Row 2: Checkboxes on mobile only */}
        <div className="flex sm:hidden items-center gap-3 px-2 pb-1.5 text-[12px]">
          <label
            className="flex items-center gap-1.5 cursor-pointer touch-manipulation select-none text-gray-400"
            onClick={() => setShowSkeleton((v) => !v)}
          >
            <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${showSkeleton ? "bg-green-600 border-green-600 text-white" : "border-neutral-500 bg-transparent"}`}>
              {showSkeleton && <span className="text-[9px] leading-none font-bold">{"\u2713"}</span>}
            </span>
            Skeleton
          </label>
          <label
            className="flex items-center gap-1.5 cursor-pointer touch-manipulation select-none text-gray-400"
            onClick={() => setShowAngles((v) => !v)}
          >
            <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${showAngles ? "bg-green-600 border-green-600 text-white" : "border-neutral-500 bg-transparent"}`}>
              {showAngles && <span className="text-[9px] leading-none font-bold">{"\u2713"}</span>}
            </span>
            Angles
          </label>
          <label
            className="flex items-center gap-1.5 cursor-pointer touch-manipulation select-none text-gray-400"
            onClick={() => setShowContacts((v) => !v)}
          >
            <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${showContacts ? "bg-green-600 border-green-600 text-white" : "border-neutral-500 bg-transparent"}`}>
              {showContacts && <span className="text-[9px] leading-none font-bold">{"\u2713"}</span>}
            </span>
            Contact
          </label>
        </div>
      </div>
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Dual video area */}
      <div className="flex flex-col md:flex-row flex-1 gap-1 p-1 min-h-0">
        {bothEmpty ? (
          <UploadPlaceholder
            label="Select two videos to compare"
            onClick={() => fileInputBothRef.current?.click()}
          />
        ) : (
          <>
            {video1 ? (
              <div className="flex-1 min-h-0 flex flex-col relative">
                <VideoPlayer
                  ref={player1Ref}
                  file={video1}
                  isActive={activePlayer === "both" || activePlayer === "video1"}
                  label="Video 1"
                  playbackSpeed={SPEEDS[speedIndex]}
                  playing={playing1}
                  onTogglePlay={() => setPlaying1((p) => !p)}
                  showSkeleton={showSkeleton}
                  showAngles={showAngles}
                  showContacts={showContacts}
                />
                <button
                  onClick={() => fileInput1Ref.current?.click()}
                  className="absolute top-2 right-2 z-10 bg-black/60 text-neutral-400 hover:text-white text-xs px-2 py-1 rounded transition-colors"
                >
                  Change
                </button>
              </div>
            ) : (
              <UploadPlaceholder label="Click to add Video 1" onClick={() => fileInput1Ref.current?.click()} />
            )}
            {video2 ? (
              <div className="flex-1 min-h-0 flex flex-col relative">
                <VideoPlayer
                  ref={player2Ref}
                  file={video2}
                  isActive={activePlayer === "both" || activePlayer === "video2"}
                  label="Video 2"
                  playbackSpeed={SPEEDS[speedIndex]}
                  playing={playing2}
                  onTogglePlay={() => setPlaying2((p) => !p)}
                  showSkeleton={showSkeleton}
                  showAngles={showAngles}
                  showContacts={showContacts}
                />
                <button
                  onClick={() => fileInput2Ref.current?.click()}
                  className="absolute top-2 right-2 z-10 bg-black/60 text-neutral-400 hover:text-white text-xs px-2 py-1 rounded transition-colors"
                >
                  Change
                </button>
              </div>
            ) : (
              <UploadPlaceholder label="Click to add Video 2" onClick={() => fileInput2Ref.current?.click()} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
